import React, { FC, useMemo, useRef } from 'react';

import {
  Button,
  Center,
  Flex,
  Heading,
  Text,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';

import { MdGroupAdd } from 'react-icons/md';

import {
  AddCustomerContactModal,
  AddCustomerContactModalRef,
} from 'components/modules/AddCustomerContact';
import {
  EditCustomerContactModal,
  EditCustomerContactModalRef,
} from 'components/modules/EditCustomerContactModal';
import { UserPill } from 'components/core/UserPill';
import { DataTable } from 'components/modules/DataTable';
import { washHub } from 'store/slices';
import { Pagination } from 'types/common';
import { Contact } from 'types/user';
import { CustomerContactsViewProps } from './CustomerContacts.props';

import { buildCustomerContactColumns } from './customerContactsColumns';

const _selected = {
  backgroundColor: 'primary.900',
  color: 'support.500',
} as any;
const _focus = { border: 'none', backgroundColor: 'primary.500' } as any;

const CustomerContactsView: FC<CustomerContactsViewProps> = (props) => {
  const {
    contacts,
    archivedContacts,
    isLoading,
    isArchivedLoading,
    pagination,
    archivedPagination,
    tabIndex,
    onTabChange,
  } = props;

  const addCustomerContactModalRef = useRef<AddCustomerContactModalRef>(null);
  const editCustomerContactModalRef = useRef<EditCustomerContactModalRef>(null);
  const selectedWashHubId = washHub.selectors.useSelectedWashHubId();
  const onCreateContact = () => {
    addCustomerContactModalRef?.current?.onOpen?.();
  };

  const columns = useMemo(() => buildCustomerContactColumns(), []);

  const renderTable = (data: Contact[], loading: boolean, pag: Pagination) => (
    <DataTable<Contact>
      minW="570px"
      data={data}
      columns={columns}
      isLoading={loading}
      pagination={pag}
      onRowClick={(contact) => {
        editCustomerContactModalRef?.current?.onOpen?.(contact);
      }}
      renderEmpty={() => (
        <Center p={8}>
          <Text fontSize="20px">No Contacts available</Text>
        </Center>
      )}
    />
  );

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex alignItems="baseline" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Customer Contacts
        </Heading>
        <Flex align="flex-end">
          <Button
            isDisabled={selectedWashHubId === ''}
            mr={2}
            minW="200px"
            leftIcon={<MdGroupAdd size="20px" />}
            onClick={onCreateContact}>
            Create Contact
          </Button>
          <UserPill />
        </Flex>
      </Flex>
      <Flex h="50px" />
      <Tabs
        variant="enclosed"
        size="lg"
        index={tabIndex}
        onChange={onTabChange}>
        <TabList
          color="support.500"
          _selected={_selected}
          _focus={_focus}
          w="100%"
          mb={4}>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Active {`(${pagination.totalCount || 0})`}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Archived {`(${archivedPagination.totalCount || 0})`}
          </Tab>
        </TabList>
        <TabPanels p={0}>
          <TabPanel p={0}>
            {renderTable(contacts, isLoading, pagination)}
          </TabPanel>
          <TabPanel p={0}>
            {renderTable(
              archivedContacts,
              isArchivedLoading,
              archivedPagination,
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
      <AddCustomerContactModal ref={addCustomerContactModalRef} />
      <EditCustomerContactModal ref={editCustomerContactModalRef} />
    </Flex>
  );
};

export default CustomerContactsView;
